import {
  SelectOptionOld,
  WSAlert,
  WSDivider,
  WSForm,
  WSInfoBox,
  WSList,
  WSSelect
} from "@wingspanhq/fe-component-library";
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import {
  useQueryIntegrationsQuickbooks,
  useQueryIntegrationsQuickbooksAccountExpenses,
  useQueryIntegrationsQuickbooksVendors
} from "../../../../query/integrations/queries";
import {
  QUICKBOOKS_ENTITY,
  RefreshModal,
  useQboRefreshModal
} from "../../../../Settings/screens/Integrations/quickbooks/RefreshButton";
import { selectorIsPayeeQboMappingEnabled } from "../../selectors/selectorIsPayeeQboMappingEnabled";
import { selectQuickbooksAccountOrItemOptions } from "../../selectors/selectQuickbooksAccountOrItemOptions";
import { selectQuickbooksUserOptions } from "../../selectors/selectQuickbooksUserOptions";

export interface PayeeQboMappingFormValues {
  qboVendorId: string;
  qboExpenseAccountId: string;
}

export const PAYEE_QBO_MAPPING_FORM_DEFAULT_VALUES: PayeeQboMappingFormValues =
  {
    qboExpenseAccountId: "",
    qboVendorId: ""
  };

export interface FormPartialPayeeQboMappingProps {
  disabled?: boolean;
}

export const FormPartialPayeeQboMapping: React.FC<
  FormPartialPayeeQboMappingProps
> = ({ disabled = false }) => {
  const queryFeatureFlags = useFeatureFlags();
  const queryIntegrationsQuickbooks = useQueryIntegrationsQuickbooks();
  const queryIntegrationsQuickbooksVendors =
    useQueryIntegrationsQuickbooksVendors();
  const queryIntegrationsQuickbooksExpenseAccounts =
    useQueryIntegrationsQuickbooksAccountExpenses();
  const qboRefreshModal = useQboRefreshModal();
  const { setValue } = useFormContext<PayeeQboMappingFormValues>();

  useEffect(() => {
    if (disabled) {
      setValue("qboVendorId", "");
      setValue("qboExpenseAccountId", "");
    }
  }, [disabled, setValue]);

  const isEnabled = selectorIsPayeeQboMappingEnabled({
    expenseAccounts: queryIntegrationsQuickbooksExpenseAccounts.data,
    featureFlags: queryFeatureFlags?.data,
    qboService: queryIntegrationsQuickbooks.data,
    vendors: queryIntegrationsQuickbooksVendors.data
  });

  if (!isEnabled) return null;

  const vendorOptions: SelectOptionOld[] = selectQuickbooksUserOptions(
    queryIntegrationsQuickbooksVendors.data
  );

  const accountOptions: SelectOptionOld[] =
    selectQuickbooksAccountOrItemOptions(
      queryIntegrationsQuickbooksExpenseAccounts.data
    );

  const defaultAccount = (
    queryIntegrationsQuickbooksExpenseAccounts.data ?? []
  ).find(
    acct =>
      acct.accountId ===
      queryIntegrationsQuickbooks.data?.defaults?.expenseAccountId
  );

  return (
    <WSList gap="L">
      <WSDivider label="QuickBooks Mapping" />
      {disabled ? (
        <WSAlert theme="warning" title="QBO disabled">
          QuickBooks mapping is per-engagement and can only be set when
          assigning a single engagement. You can always configure mapping later
          from the contractor's details page.
        </WSAlert>
      ) : (
        <WSInfoBox>
          Because you have an active integration with QBO, please map this
          contractor to an existing Vendor, or leave blank and we will create a
          new one. You can also set a default expense account for payable line
          items.
        </WSInfoBox>
      )}
      <WSForm.Field
        name="qboVendorId"
        component={WSSelect}
        componentProps={{
          disabled,
          internalSearch: true,
          menuFooterAction: {
            icon: "refresh-v",
            label: "Resync QBO Vendors",
            onClick: () => {
              qboRefreshModal.open({ entity: QUICKBOOKS_ENTITY.VENDORS });
            }
          },
          options: disabled ? [] : vendorOptions,
          placeholder: "Not mapped, create new Vendor"
        }}
        label="QBO Vendor"
      />
      <WSForm.Field
        name="qboExpenseAccountId"
        component={WSSelect}
        componentProps={{
          disabled,
          internalSearch: true,
          menuFooterAction: {
            icon: "refresh-v",
            label: "Resync QBO Expenses Accounts",
            onClick: () => {
              qboRefreshModal.open({ entity: QUICKBOOKS_ENTITY.EXPENSES });
            }
          },
          options: disabled ? [] : accountOptions,
          placeholder: `Use default "${defaultAccount?.fullyQualifiedName}"`
        }}
        label="Default QBO Expense Account"
      />
      <RefreshModal />
    </WSList>
  );
};
